/**
 * The gate: sign-in, and every state in which the platform will not open.
 *
 * No credential is asked for here. The button navigates to the identity
 * provider, on the provider's own domain. A form of our own would make this
 * application an intermediary that sees passwords, and there is no reason for
 * it to see them.
 *
 * The same screen covers the degraded state on purpose: when a session cannot
 * be validated safely, this is all there is to see. No last balance, no
 * entries, no charts, no cached figures.
 *
 * Which state is shown comes from the query string the backend redirects to
 * (`/callback` writes `?error=1`, `?degraded=1`, `?forbidden=1`) plus one that
 * only the client can know — an expired session — passed in as a prop.
 */

import type { SessionInfo } from "../api/types";
import { api } from "../api/client";
import { AuthCard, AuthContext, AuthNotice, AuthShell, ExternalGlyph } from "../components/auth";

export type Reason = "signin" | "expired" | "degraded" | "forbidden" | "error" | "unconfigured";

function reasonFromUrl(session: SessionInfo | null, expired: boolean): Reason {
  const params = new URLSearchParams(window.location.search);
  if (session?.degraded || params.has("degraded")) return "degraded";
  if (params.has("forbidden")) return "forbidden";
  if (params.has("error")) return "error";
  if (session?.provider === "none") return "unconfigured";
  // Two ways in, same state: the prop when `App` notices it in place, and the
  // parameter when `client.ts` had to navigate here from another screen. It is
  // checked last so it never competes with what the provider redirected for.
  if (expired || params.has("expired")) return "expired";
  return "signin";
}

interface Copy {
  title: string;
  body: string;
  action: string | null;
  /** The action leaves this origin, so it is announced before it is taken. */
  redirects?: boolean;
  /** Offer to leave the provider and come back as somebody else. */
  switchAccount?: boolean;
  tone?: "warn" | "critical";
}

const COPY: Record<Reason, Copy> = {
  signin: {
    title: "Iniciar sesión",
    body:
      "El acceso se valida mediante el proveedor de identidad de la organización. " +
      "Esta pantalla no gestiona contraseñas directamente.",
    action: "Continuar al acceso institucional",
    redirects: true,
  },
  expired: {
    title: "Sesión finalizada",
    body: "Su sesión ha finalizado. Inicie sesión nuevamente para continuar.",
    action: "Iniciar sesión nuevamente",
    redirects: true,
  },
  degraded: {
    title: "Servicio temporalmente no disponible",
    body:
      "No se pudo validar su sesión de forma segura. Por protección de la " +
      "información contable, el acceso ha sido bloqueado.",
    action: "Reintentar",
    tone: "warn",
  },
  forbidden: {
    title: "Acceso no autorizado",
    body:
      "Su cuenta no dispone de permisos para acceder a esta plataforma. " +
      "Solicítelos al administrador, o ingrese con otra cuenta si dispone de una.",
    action: null,
    switchAccount: true,
    tone: "warn",
  },
  error: {
    title: "No fue posible completar el acceso",
    body:
      "El intento de inicio de sesión no llegó a completarse. Vuelva a " +
      "intentarlo; si el problema persiste, ingrese con otra cuenta.",
    action: "Reintentar acceso",
    redirects: true,
    switchAccount: true,
    tone: "warn",
  },
  unconfigured: {
    title: "Acceso bloqueado",
    body:
      "Esta instalación no tiene proveedor de identidad configurado, así que no " +
      "entrega información. Avise al administrador del sistema.",
    action: null,
    tone: "critical",
  },
};

/**
 * Same-origin paths only.
 *
 * `/auth/start` validates this again on the server — that is the actual
 * guarantee — but an external `next` should not even get as far as being
 * painted into a link on this page: resolving it against the current origin
 * discards absolute URLs and the backslash ones some browsers read as slashes.
 */
function safeNext(value: string | null): string {
  if (!value) return "/";
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return "/";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

export function Login({
  session,
  expired = false,
}: {
  session: SessionInfo | null;
  expired?: boolean;
}) {
  const reason = reasonFromUrl(session, expired);
  const copy = COPY[reason];
  const next = safeNext(new URLSearchParams(window.location.search).get("next"));
  // Degraded means the provider could not be reached; sending the browser to it
  // would only fail again, so the retry reloads this page instead.
  const retry = reason === "degraded" ? window.location.pathname : api.loginUrl(next);

  return (
    <AuthShell context={<AuthContext />}>
      <AuthCard title={copy.title} tone={copy.tone}>
        <p className="auth-body">{copy.body}</p>

        <div className="auth-actions">
          {copy.action && (
            <a className="auth-action primary" href={retry}>
              {copy.redirects && <ExternalGlyph />}
              {copy.action}
            </a>
          )}
          {copy.switchAccount && (
            /* `/logout` ends the session here AND at the provider. Without
               that, "try again" would return exactly the same identity — the
               provider still has it open — and the screen would repeat itself
               with nothing changed. */
            <a className="auth-action" href={api.logoutUrl()}>
              Ingresar con otra cuenta
            </a>
          )}
        </div>

        {copy.redirects && (
          <AuthNotice>Será redirigido al servicio de autenticación institucional.</AuthNotice>
        )}

        <p className="auth-support">
          Si presenta inconvenientes para ingresar, contacte al administrador de la plataforma.
        </p>
      </AuthCard>
    </AuthShell>
  );
}
